import * as React from 'react';

import { useListingScrollContainer } from '../../../common/containers/ListingScrollContainer';

/**
 * Порог, ниже которого фильтры не прячем. Равен высоте чипса: пока список пролистан меньше,
 * прятать нечего — фильтры и так на своём месте, а дёргать их на первых пикселях жеста незачем.
 */
const SCROLL_HIDE_THRESHOLD = 36;
/** Минимальная дельта скролла для смены состояния — защита от дребезга. */
const SCROLL_DIRECTION_DELTA = 4;
/**
 * Пауза на определение направления после смены состояния. Схлопывание фильтров уменьшает панель,
 * шторка следом вырастает вверх, и браузер подправляет позицию скролла, чтобы контент остался на
 * месте. Отличить такую подвижку от жеста пользователя по `scrollTop` нельзя, а читается она как
 * скролл в обратную сторону — и фильтры начинают моргать. Поэтому на время анимации направление
 * не пересчитываем.
 */
const STATE_CHANGE_COOLDOWN_MS = 350;

/**
 * Скрыты ли быстрые фильтры скроллом списка.
 *
 * Список живёт в шторке и скроллится не окном, а своим контейнером — его шторка отдаёт через
 * `ListingScrollContainerContext`. Листаем вниз — фильтры уезжают и освобождают место выдаче,
 * листаем вверх — возвращаются.
 *
 * @param isEnabled следить за скроллом имеет смысл только у развёрнутой на весь экран шторки
 */
export function useQuickFiltersScrollState(isEnabled: boolean): boolean {
  const [isHidden, setIsHidden] = React.useState(false);
  const container = useListingScrollContainer();

  React.useEffect(() => {
    if (!isEnabled) {
      setIsHidden(false);

      return;
    }

    if (!container) {
      return;
    }

    // Переходами владеет только этот обработчик, поэтому текущее состояние держим рядом с ним:
    // так решение о следующем состоянии и отметка времени о смене живут в одном месте.
    let currentIsHidden = false;
    let lastScrollTop = container.scrollTop;
    let lastChangeTime = 0;
    let isTicking = false;

    const handleScroll = () => {
      const { scrollTop } = container;
      const scrollDelta = scrollTop - lastScrollTop;
      const isAnimating = Date.now() - lastChangeTime < STATE_CHANGE_COOLDOWN_MS;

      lastScrollTop = scrollTop;
      isTicking = false;

      let nextIsHidden = currentIsHidden;

      if (scrollTop <= SCROLL_HIDE_THRESHOLD) {
        nextIsHidden = false;
      } else if (!isAnimating) {
        if (scrollDelta > SCROLL_DIRECTION_DELTA) {
          nextIsHidden = true;
        } else if (-scrollDelta > SCROLL_DIRECTION_DELTA) {
          nextIsHidden = false;
        }
      }

      if (currentIsHidden === nextIsHidden) {
        return;
      }

      currentIsHidden = nextIsHidden;
      lastChangeTime = Date.now();
      setIsHidden(nextIsHidden);
    };

    const requestScrollHandling = () => {
      if (!isTicking) {
        isTicking = true;
        requestAnimationFrame(handleScroll);
      }
    };

    container.addEventListener('scroll', requestScrollHandling, { passive: true });

    return () => {
      container.removeEventListener('scroll', requestScrollHandling);
    };
  }, [container, isEnabled]);

  return isHidden;
}
